import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FitnessPlanService } from '../../fitness-plan.service';
import { CopyDayDialogComponent } from '../copy-day-dialog/copy-day-dialog.component';

interface ExerciseSet {
  setNumber: number;
  reps: string;
  time: string;
  timeMinutes?: number;
  timeSeconds?: number;
  totalSeconds?: number; // New field to store calculated total seconds
  weights: string;
  status: 'new' | 'existing';
}

interface Exercise {
  id: number;
  exercise: string;
  exerciseId?: number;
  sets: ExerciseSet[];
  section?: 'warmup' | 'main' | 'cooldown';
  allotmentId?: number;
}

interface DayTemplate {
  dayName: string;
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, etc.
  isExpanded: boolean;
  exercises: Exercise[];
  isRestDay: boolean;
}

@Component({
  selector: 'app-create-template',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatDialogModule,
  ],
  templateUrl: './create-template.component.html',
  styleUrl: './create-template.component.css',
})
export class CreateTemplateComponent implements OnInit {
  // Input to control if the component is embedded (hides back button)
  @Input() isEmbedded: boolean = false;

  // Event emitter for when template is successfully created
  @Output() templateCreated = new EventEmitter<void>();

  // Template details
  templateName: string = '';
  templateDescription: string = '';

  // Exercise data
  availableExercises: string[] = [];
  availableExercisesData: any[] = [];

  // Exercise section configurations
  exerciseSections = [
    {
      key: 'warmup' as 'warmup',
      title: '🔥 Warm Up Exercises',
      color: '#28a745',
      background: '#f8fffe',
      border: '#e8f5e8',
      buttonBg: '#28a745',
    },
    {
      key: 'main' as 'main',
      title: '💪 Main Exercises',
      color: '#05445e',
      background: '#f8f9ff',
      border: '#e8e8ff',
      buttonBg: '#05445e',
    },
    {
      key: 'cooldown' as 'cooldown',
      title: '🧘 Cool Down Exercises',
      color: '#17a2b8',
      background: '#f8fdff',
      border: '#e8f4f8',
      buttonBg: '#17a2b8',
    },
  ];

  // Weekly template structure (7 days)
  weeklyTemplate: DayTemplate[] = [];

  // Validation properties
  showValidationErrors: boolean = false;
  validationErrors: string[] = [];

  // Success properties
  showSuccessModal: boolean = false;

  // Delete confirmation properties
  showDeleteConfirmation: boolean = false;

  // Empty days warning properties
  showEmptyDaysWarning: boolean = false;
  emptyDaysCount: number = 0;
  pendingSubmitAction: (() => void) | null = null;

  // Component mode
  mode: 'create' | 'view' | 'edit' = 'create';
  templateId: string | null = null;
  isReadonly: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private fitnessPlanService: FitnessPlanService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Determine mode based on route
    const path = this.router.url;
    if (path.includes('/view/')) {
      this.mode = 'view';
      this.isReadonly = true;
      this.title.setTitle('View Template');
    } else if (path.includes('/edit/')) {
      this.mode = 'edit';
      this.title.setTitle('Edit Template');
    } else {
      this.mode = 'create';
      this.title.setTitle('Add Exercises to the Template');
    }

    // Get template ID from route params
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.templateId = params['id'];
        if (this.templateId) {
          this.loadTemplate(this.templateId);
        }
      }
    });

    // Check for duplicate parameter
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams['duplicate']) {
        this.loadTemplate(queryParams['duplicate'], true);
      }
    });

    this.loadExercises()
      .then(() => {
        // console.log('Exercises loaded successfully');
      })
      .catch((error) => {
        console.error('Error loading exercises:', error);
      });
    this.initializeWeeklyTemplate();
  }

  loadTemplate(templateId: string, isDuplicate: boolean = false): void {
    if (!templateId) return;

    // First ensure exercises are loaded, then load template data
    if (this.availableExercisesData.length === 0) {
      this.loadExercises()
        .then(() => {
          this.loadTemplateData(templateId, isDuplicate);
        })
        .catch((error) => {
          console.error('Error loading exercises before template:', error);
          // Continue anyway, might still work
          this.loadTemplateData(templateId, isDuplicate);
        });
    } else {
      this.loadTemplateData(templateId, isDuplicate);
    }
  }

  private loadTemplateData(
    templateId: string,
    isDuplicate: boolean = false
  ): void {
    this.fitnessPlanService.getFitnessPlanTemplateById(templateId).subscribe(
      (response: any) => {
        // console.log('Template loaded:', response);
        this.populateTemplateData(response, isDuplicate);
      },
      (error: any) => {
        console.error('Error loading template:', error);
        // Handle error - maybe redirect back to templates list
        this.router.navigate(['/fitness-plan/templates']);
      }
    );
  }

  populateTemplateData(templateData: any, isDuplicate: boolean = false): void {
    // console.log('Populating template data:', templateData);
    // console.log(
    //   'Available exercises count:',
    //   this.availableExercisesData.length
    // );
    // console.log('Weekly template initialized:', this.weeklyTemplate.length);

    if (isDuplicate) {
      this.templateName = templateData.name + ' (Copy)';
      this.templateDescription = templateData.description;
      this.mode = 'create';
      this.isReadonly = false;
      this.title.setTitle('Create Template (Copy)');
    } else {
      this.templateName = templateData.name || '';
      this.templateDescription = templateData.description || '';
    }

    // console.log('Template name set to:', this.templateName);
    // console.log('Template description set to:', this.templateDescription);

    // Ensure weekly template is initialized
    if (this.weeklyTemplate.length === 0) {
      // console.log('Weekly template not initialized, initializing now...');
      this.initializeWeeklyTemplate();
    }

    // Populate days and exercises
    if (templateData.days && templateData.days.length > 0) {
      // console.log('Template has', templateData.days.length, 'days to populate');
      templateData.days.forEach((day: any, dayIndex: number) => {
        // console.log('Processing day:', day);
        const targetDayIndex = (day.dayOfWeek - 1) % 7; // Convert back to 0-based index
        if (targetDayIndex >= 0 && targetDayIndex < 7) {
          this.weeklyTemplate[targetDayIndex].isRestDay =
            day.isRestDay || false;
          if (day.exercises && day.exercises.length > 0) {
            // console.log('Day has', day.exercises.length, 'exercises');
            // console.log('Exercises details:', day.exercises);

            this.weeklyTemplate[targetDayIndex].exercises = day.exercises.map(
              (exercise: any) => {
                const exerciseName = this.getExerciseNameById(
                  exercise.exerciseId
                );
                // console.log(
                //   'Exercise ID:',
                //   exercise.exerciseId,
                //   'mapped to name:',
                //   exerciseName
                // );
                // console.log('Exercise section:', exercise.section);
                // console.log('Exercise raw data:', exercise);

                return {
                  id: exercise.id || Math.random(),
                  exercise: exerciseName || 'Unknown Exercise',
                  exerciseId: exercise.exerciseId,
                  section: exercise.section || 'main',
                  sets:
                    exercise.sets && exercise.sets.length > 0
                      ? exercise.sets.map((set: any, index: number) => ({
                          setNumber: index + 1,
                          reps: set.reps || '',
                          time: this.convertSecondsToTimeString(set.time || 0),
                          timeMinutes: Math.floor((set.time || 0) / 60),
                          timeSeconds: (set.time || 0) % 60,
                          totalSeconds: set.time || 0,
                          weights: set.weights || '',
                          status: 'existing',
                        }))
                      : [
                          {
                            setNumber: 1,
                            reps: '',
                            time: '',
                            timeMinutes: 0,
                            timeSeconds: 0,
                            totalSeconds: 0,
                            weights: '',
                            status: 'new',
                          },
                        ],
                };
              }
            );

            // Log the final exercises for this day
            // console.log(
            //   'Final exercises for day',
            //   targetDayIndex,
            //   ':',
            //   this.weeklyTemplate[targetDayIndex].exercises
            // );
          } else {
            // console.log('Day has no exercises, keeping default structure');
          }
        } else {
          console.warn('Invalid day index:', targetDayIndex, 'for day:', day);
        }
      });
    } else {
      // console.log('Template has no days data to populate');
    }

    // console.log('Final weekly template state:', this.weeklyTemplate);

    // Debug: Check all exercise sections in the loaded template
    this.weeklyTemplate.forEach((day, dayIndex) => {
      if (day.exercises && day.exercises.length > 0) {
        // console.log(
        //   `Day ${dayIndex} (${day.dayName}) exercises:`,
        //   day.exercises.map((ex) => ({
        //     name: ex.exercise,
        //     section: ex.section,
        //   }))
        // );

        // Count exercises by section
        const sectionCounts = {
          warmup: this.getExercisesBySection(dayIndex, 'warmup').length,
          main: this.getExercisesBySection(dayIndex, 'main').length,
          cooldown: this.getExercisesBySection(dayIndex, 'cooldown').length,
        };
        // console.log(`Day ${dayIndex} section counts:`, sectionCounts);
      }
    });

    // Ensure all sections have exercises after loading template
    this.ensureAllSectionsHaveExercises();
  }

  getExerciseNameById(exerciseId: number): string {
    const exercise = this.availableExercisesData.find(
      (ex) => ex.id === exerciseId
    );
    return exercise ? exercise.name : 'Unknown Exercise';
  }

  loadExercises(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fitnessPlanService.getExercises().subscribe(
        (response: any) => {
          if (
            response &&
            response.exercises &&
            Array.isArray(response.exercises)
          ) {
            this.availableExercisesData = response.exercises;
            this.availableExercises = response.exercises.map(
              (exercise: any) => exercise.name || exercise
            );
            // console.log(
            //   'Loaded exercises for template:',
            //   this.availableExercises.length
            // );
            resolve();
          } else {
            console.warn(
              'Invalid exercises response format, using empty array'
            );
            this.availableExercises = [];
            this.availableExercisesData = [];
            resolve();
          }
        },
        (error) => {
          console.error('Error loading exercises:', error);
          this.availableExercises = [];
          this.availableExercisesData = [];
          reject(error);
        }
      );
    });
  }

  initializeWeeklyTemplate(): void {
    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    this.weeklyTemplate = daysOfWeek.map((dayName, index) => ({
      dayName,
      dayOfWeek: index,
      isExpanded: index === 0, // Expand the first day by default
      isRestDay: false,
      exercises: [
        {
          id: 1,
          exercise: '',
          sets: [
            {
              setNumber: 1,
              reps: '',
              time: '',
              timeMinutes: 0,
              timeSeconds: 0,
              totalSeconds: 0,
              weights: '',
              status: 'new',
            },
          ],
          section: 'warmup' as const,
        },
        {
          id: 2,
          exercise: '',
          sets: [
            {
              setNumber: 1,
              reps: '',
              time: '',
              timeMinutes: 0,
              timeSeconds: 0,
              totalSeconds: 0,
              weights: '',
              status: 'new',
            },
          ],
          section: 'main' as const,
        },
        {
          id: 3,
          exercise: '',
          sets: [
            {
              setNumber: 1,
              reps: '',
              time: '',
              timeMinutes: 0,
              timeSeconds: 0,
              totalSeconds: 0,
              weights: '',
              status: 'new',
            },
          ],
          section: 'cooldown' as const,
        },
      ],
    }));
  }

  toggleAccordion(index: number): void {
    this.weeklyTemplate[index].isExpanded =
      !this.weeklyTemplate[index].isExpanded;
  }

  toggleDayRestDay(index: number): void {
    const dayTemplate = this.weeklyTemplate[index];
    dayTemplate.isRestDay = !dayTemplate.isRestDay;

    if (dayTemplate.isRestDay) {
      // Clear exercises when marking as rest day
      dayTemplate.exercises = [];
    } else {
      // Restore default exercise structure when switching from rest day
      dayTemplate.exercises = [
        {
          id: 1,
          exercise: '',
          sets: [
            {
              setNumber: 1,
              reps: '',
              time: '',
              timeMinutes: 0,
              timeSeconds: 0,
              totalSeconds: 0,
              weights: '',
              status: 'new',
            },
          ],
          section: 'warmup' as const,
        },
        {
          id: 2,
          exercise: '',
          sets: [
            {
              setNumber: 1,
              reps: '',
              time: '',
              timeMinutes: 0,
              timeSeconds: 0,
              totalSeconds: 0,
              weights: '',
              status: 'new',
            },
          ],
          section: 'main' as const,
        },
        {
          id: 3,
          exercise: '',
          sets: [
            {
              setNumber: 1,
              reps: '',
              time: '',
              timeMinutes: 0,
              timeSeconds: 0,
              totalSeconds: 0,
              weights: '',
              status: 'new',
            },
          ],
          section: 'cooldown' as const,
        },
      ];
    }
  }

  addExerciseToDay(
    dayIndex: number,
    section: 'warmup' | 'main' | 'cooldown' = 'main'
  ): void {
    const exercises = this.weeklyTemplate[dayIndex].exercises;
    const newId = Math.max(0, ...exercises.map((e) => e.id)) + 1;
    exercises.push({
      id: newId,
      exercise: '',
      sets: [
        {
          setNumber: 1,
          reps: '',
          time: '',
          timeMinutes: 0,
          timeSeconds: 0,
          totalSeconds: 0,
          weights: '',
          status: 'new',
        },
      ],
      section: section,
    });
  }

  removeExerciseFromDay(dayIndex: number, exerciseId: number): void {
    const day = this.weeklyTemplate[dayIndex];
    const exerciseToRemove = day.exercises.find((e) => e.id === exerciseId);
    if (!exerciseToRemove) return;

    const section = exerciseToRemove.section;
    day.exercises = day.exercises.filter((e) => e.id !== exerciseId);

    // If the section becomes empty, add a placeholder
    const sectionHasExercises = day.exercises.some(
      (e) => e.section === section
    );
    if (!sectionHasExercises) {
      this.addExerciseToDay(dayIndex, section);
    }
  }

  addSetToExercise(dayIndex: number, exerciseId: number): void {
    const exercise = this.weeklyTemplate[dayIndex].exercises.find(
      (e) => e.id === exerciseId
    );
    if (exercise) {
      const newSetNumber = exercise.sets.length + 1;
      const newSet: ExerciseSet = {
        setNumber: newSetNumber,
        reps: '',
        time: '',
        timeMinutes: 0,
        timeSeconds: 0,
        totalSeconds: 0,
        weights: '',
        status: 'new',
      };
      exercise.sets.push(newSet);
    }
  }

  removeSetFromExercise(
    dayIndex: number,
    exerciseId: number,
    setIndex: number
  ): void {
    const exercise = this.weeklyTemplate[dayIndex].exercises.find(
      (e) => e.id === exerciseId
    );
    if (exercise && exercise.sets.length > 1) {
      exercise.sets.splice(setIndex, 1);
      // Renumber remaining sets
      exercise.sets.forEach((set, index) => {
        set.setNumber = index + 1;
      });
    }
  }

  getExercisesBySection(
    dayIndex: number,
    section: 'warmup' | 'main' | 'cooldown'
  ): Exercise[] {
    return this.weeklyTemplate[dayIndex].exercises.filter(
      (exercise) => exercise.section === section
    );
  }

  getSectionDisplayName(section: 'warmup' | 'main' | 'cooldown'): string {
    const sectionInfo = this.exerciseSections.find((s) => s.key === section);
    return sectionInfo ? sectionInfo.title : 'Exercise';
  }

  getSectionExerciseCount(
    dayIndex: number,
    section: 'warmup' | 'main' | 'cooldown'
  ): number {
    return this.getExercisesBySection(dayIndex, section).filter(
      (exercise) => exercise.exercise && exercise.exercise.trim() !== ''
    ).length;
  }

  // Method to update total seconds when minutes or seconds change
  updateTotalSeconds(exercise: Exercise, setIndex: number): void {
    const set = exercise.sets[setIndex];
    const minutes = Number(set.timeMinutes) || 0;
    const seconds = Number(set.timeSeconds) || 0;
    set.totalSeconds = minutes * 60 + seconds;

    console.log(`Updated total seconds for set ${setIndex + 1}:`, {
      minutes,
      seconds,
      totalSeconds: set.totalSeconds,
    });
  }

  // Convert total seconds to time string (MM:SS) (copied from create-fitness-plan)
  convertSecondsToTimeString(seconds: number): string {
    if (!seconds || seconds === 0) {
      return '';
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  }

  validateTemplate(): string[] {
    const errors: string[] = [];

    if (!this.templateName.trim()) {
      errors.push('Template name is required.');
    }

    const hasAtLeastOneExercise = this.weeklyTemplate.some(
      (day) =>
        !day.isRestDay && day.exercises.some((ex) => ex.exercise.trim() !== '')
    );

    if (!hasAtLeastOneExercise) {
      errors.push(
        'The template must have at least one exercise on a non-rest day.'
      );
    }

    this.weeklyTemplate.forEach((day, dayIndex) => {
      if (day.isRestDay) return;

      day.exercises.forEach((exercise) => {
        if (exercise.exercise.trim()) {
          const hasRepsOrTime = exercise.sets.some(
            (set) =>
              (set.reps && set.reps.trim() !== '0' && set.reps.trim() !== '') ||
              (set.time && set.time.trim() !== '0' && set.time.trim() !== '') ||
              (set.timeMinutes && set.timeMinutes > 0) ||
              (set.timeSeconds && set.timeSeconds > 0)
          );

          if (!hasRepsOrTime) {
            errors.push(
              `Exercise "${exercise.exercise}" on ${day.dayName} must have at least one set with reps or time.`
            );
          }
        }
      });
    });

    return errors;
  }

  saveTemplate(): void {
    // Basic template validation
    if (!this.templateName.trim()) {
      this.validationErrors = ['Template name is required.'];
      this.showValidationErrors = true;
      return;
    }

    // Validate all exercises before submission
    const validationErrors = this.validateAllExercises();

    if (validationErrors.length > 0) {
      this.validationErrors = validationErrors;
      this.showValidationErrors = true;
      return; // Don't proceed with submission
    }

    // Check for empty days before submission
    const emptyDaysCount = this.checkForEmptyDays();

    if (emptyDaysCount > 0) {
      this.emptyDaysCount = emptyDaysCount;
      this.showEmptyDaysWarning = true;
      this.pendingSubmitAction = () => this.executeSubmission();
      return; // Show warning and wait for user confirmation
    }

    // No empty days, proceed with submission directly
    this.executeSubmission();
  }

  private executeSubmission(): void {
    const templateData = {
      name: this.templateName,
      description: this.templateDescription,
      days: this.weeklyTemplate.map((day) => ({
        dayOfWeek: day.dayOfWeek + 1, // Change to start with 1 instead of 0
        dayName: day.dayName,
        isRestDay: day.isRestDay,
        exercises: day.isRestDay
          ? []
          : day.exercises
              .filter((ex) => ex.exercise.trim() !== '')
              .map((ex) => {
                const exerciseData = this.availableExercisesData.find(
                  (e) => e.name === ex.exercise
                );
                return {
                  exerciseId: exerciseData ? exerciseData.id : null,
                  section: ex.section,
                  sets: ex.sets.map((set) => ({
                    reps:
                      set.reps === '' ||
                      set.reps === null ||
                      set.reps === undefined
                        ? 0
                        : Number(set.reps),
                    time:
                      set.totalSeconds ||
                      (set.timeMinutes || 0) * 60 + (set.timeSeconds || 0), // Send total seconds
                    weights:
                      set.weights === '' ||
                      set.weights === null ||
                      set.weights === undefined
                        ? 0
                        : Number(set.weights),
                  })),
                };
              }),
      })),
    };

    // console.log('Saving template data:', JSON.stringify(templateData, null, 2));

    // Use different API calls based on mode
    if (this.mode === 'edit' && this.templateId) {
      this.fitnessPlanService
        .updateFitnessPlanTemplate(this.templateId, templateData)
        .subscribe(
          (response: any) => {
            console.log('Template updated successfully', response);
            this.showSuccessModal = true;
            this.templateCreated.emit(); // Emit event when template is updated
          },
          (error: any) => {
            console.error('Error updating template', error);
            this.validationErrors = [
              'An unexpected error occurred while updating the template. Please try again.',
            ];
            this.showValidationErrors = true;
          }
        );
    } else {
      this.fitnessPlanService.createFitnessPlanTemplate(templateData).subscribe(
        (response: any) => {
          console.log('Template saved successfully', response);
          this.showSuccessModal = true;
          this.templateCreated.emit(); // Emit event when template is created
        },
        (error: any) => {
          console.error('Error saving template', error);
          this.validationErrors = [
            'An unexpected error occurred while saving the template. Please try again.',
          ];
          this.showValidationErrors = true;
        }
      );
    }
  }

  closeValidationModal(): void {
    this.showValidationErrors = false;
  }

  // Method to close validation error popup
  closeValidationErrors(): void {
    this.showValidationErrors = false;
    this.validationErrors = [];
  }

  // Method to check for empty days (days with no exercises)
  private checkForEmptyDays(): number {
    let emptyDaysCount = 0;

    this.weeklyTemplate.forEach((day) => {
      // Skip rest days as they are intentionally empty
      if (day.isRestDay) {
        return;
      }

      // Check if day has no exercises or all exercises are empty
      const hasValidExercises = day.exercises.some((exercise) => {
        return exercise.exercise && exercise.exercise.trim() !== '';
      });

      if (!hasValidExercises) {
        emptyDaysCount++;
      }
    });

    return emptyDaysCount;
  }

  // Method to confirm and continue with empty days submission
  confirmEmptyDaysSubmission(): void {
    this.showEmptyDaysWarning = false;
    if (this.pendingSubmitAction) {
      this.pendingSubmitAction();
      this.pendingSubmitAction = null;
    }
  }

  // Method to cancel empty days warning
  cancelEmptyDaysSubmission(): void {
    this.showEmptyDaysWarning = false;
    this.pendingSubmitAction = null;
    this.emptyDaysCount = 0;
  }

  // Method to validate all exercises across all days
  validateAllExercises(): string[] {
    const errors: string[] = [];

    this.weeklyTemplate.forEach((day, dayIndex) => {
      const dayDisplay = day.dayName;

      // Skip validation for rest days
      if (day.isRestDay) {
        return;
      }

      // Check each exercise in the day
      day.exercises.forEach((exercise, exerciseIndex) => {
        // Skip empty exercises (exercises without a name)
        if (!exercise.exercise.trim()) {
          return;
        }

        // console.log(
        //   `DEBUG: Validating exercise "${exercise.exercise}" on ${dayDisplay}:`,
        //   {
        //     exercise: exercise.exercise,
        //     section: exercise.section,
        //     sets: exercise.sets.map((set) => ({
        //       setNumber: set.setNumber,
        //       reps: set.reps,
        //       time: set.time,
        //       timeMinutes: set.timeMinutes,
        //       timeSeconds: set.timeSeconds,
        //       weights: set.weights,
        //     })),
        //   }
        // );

        const sectionName = this.getSectionDisplayNameForValidation(
          exercise.section || 'main'
        );
        const exerciseIdentifier = `${dayDisplay} - ${sectionName} - ${exercise.exercise}`;

        // Check if the exercise has at least one valid set
        const hasValidSet = exercise.sets.some((set, setIndex) => {
          const repsValue =
            typeof set.reps === 'string'
              ? set.reps.trim()
              : set.reps !== null && set.reps !== undefined
              ? String(set.reps).trim()
              : '';

          // Check for time - prioritize separate minute/second fields over time string
          let hasValidTime = false;

          // First check separate minute/second fields (used by the UI)
          const minutes = Number(set.timeMinutes) || 0;
          const seconds = Number(set.timeSeconds) || 0;
          hasValidTime = minutes > 0 || seconds > 0;

          // If no separate fields, check time string as fallback
          if (!hasValidTime && typeof set.time === 'string') {
            const timeValue = set.time.trim();
            hasValidTime = !!(
              timeValue &&
              timeValue !== '0' &&
              timeValue !== '00:00'
            );
          }

          const weightsValue =
            typeof set.weights === 'string'
              ? set.weights.trim()
              : set.weights !== null && set.weights !== undefined
              ? String(set.weights).trim()
              : '';

          // A set is valid if it has reps OR time (weights alone is not sufficient for a complete exercise)
          const hasValidReps =
            repsValue && repsValue !== '0' && repsValue !== '';

          return hasValidReps || hasValidTime;
        });

        if (!hasValidSet) {
          errors.push(
            `${exerciseIdentifier}: Must have at least one set with either reps or time specified.`
          );
        }

        // Additional validation: Check for sets with only weights but no reps or time
        exercise.sets.forEach((set, setIndex) => {
          const repsValue =
            typeof set.reps === 'string'
              ? set.reps.trim()
              : set.reps !== null && set.reps !== undefined
              ? String(set.reps).trim()
              : '';

          // Check for time - prioritize separate minute/second fields over time string
          let hasTime = false;

          // First check separate minute/second fields (used by the UI)
          const minutes = Number(set.timeMinutes) || 0;
          const seconds = Number(set.timeSeconds) || 0;
          hasTime = minutes > 0 || seconds > 0;

          // If no separate fields, check time string as fallback
          if (!hasTime && typeof set.time === 'string') {
            const timeValue = set.time.trim();
            hasTime = !!(
              timeValue &&
              timeValue !== '0' &&
              timeValue !== '00:00'
            );
          }

          const weightsValue =
            typeof set.weights === 'string'
              ? set.weights.trim()
              : set.weights !== null && set.weights !== undefined
              ? String(set.weights).trim()
              : '';

          const hasReps = repsValue && repsValue !== '0' && repsValue !== '';
          const hasWeights =
            weightsValue && weightsValue !== '0' && weightsValue !== '';

          // If a set has only weights but no reps or time, it's incomplete
          if (hasWeights && !hasReps && !hasTime) {
            errors.push(
              `${exerciseIdentifier} - Set ${set.setNumber}: Cannot have weights without reps or time. Please specify either reps or time.`
            );
          }

          // If both reps and time are entered in the same set, show error
          if (hasReps && hasTime) {
            errors.push(
              `${exerciseIdentifier} - Set ${set.setNumber}: Please enter either reps or time, not both.`
            );
          }
        });

        // Validation: All sets in an exercise must use the same type (reps or time) as the first valid set
        if (exercise.sets.length > 0) {
          // Find the first set that has either reps or time filled
          let firstValidSetIndex = -1;
          let firstSetType: 'reps' | 'time' | null = null;

          for (let i = 0; i < exercise.sets.length; i++) {
            const set = exercise.sets[i];
            const repsValue =
              typeof set.reps === 'string'
                ? set.reps.trim()
                : set.reps !== null && set.reps !== undefined
                ? String(set.reps).trim()
                : '';

            // Check for time - prioritize separate minute/second fields over time string
            let hasTime = false;

            // First check separate minute/second fields (used by the UI)
            const minutes = Number(set.timeMinutes) || 0;
            const seconds = Number(set.timeSeconds) || 0;
            hasTime = minutes > 0 || seconds > 0;

            // If no separate fields, check time string as fallback
            if (!hasTime && typeof set.time === 'string') {
              const timeValue = set.time.trim();
              hasTime = !!(
                timeValue &&
                timeValue !== '0' &&
                timeValue !== '00:00'
              );
            }

            const hasReps = repsValue && repsValue !== '0' && repsValue !== '';

            // Skip sets that have neither reps nor time
            if (!hasReps && !hasTime) {
              continue;
            }

            // This is the first set with data
            if (firstValidSetIndex === -1) {
              firstValidSetIndex = i;
              // Only set the type if the set has EITHER reps OR time, not both
              if (hasReps && !hasTime) {
                firstSetType = 'reps';
              } else if (hasTime && !hasReps) {
                firstSetType = 'time';
              } else if (hasReps && hasTime) {
                // This case is already handled above in the same-set validation
                firstSetType = null; // Don't set a type for inconsistent first set
              }
              continue;
            }

            // Check consistency with the first valid set (only if first set was consistent)
            if (firstSetType === 'reps' && hasTime) {
              errors.push(
                `${exerciseIdentifier} - Set ${set.setNumber}: All sets for this exercise must use reps (like Set ${exercise.sets[firstValidSetIndex].setNumber}). Please remove time and add reps.`
              );
            }
            if (firstSetType === 'time' && hasReps) {
              errors.push(
                `${exerciseIdentifier} - Set ${set.setNumber}: All sets for this exercise must use time (like Set ${exercise.sets[firstValidSetIndex].setNumber}). Please remove reps and add time.`
              );
            }
          }
        }
      });
    });

    return errors;
  }

  getSectionDisplayNameForValidation(
    section: 'warmup' | 'main' | 'cooldown'
  ): string {
    const sectionNames = {
      warmup: 'Warm Up',
      main: 'Main Exercise',
      cooldown: 'Cool Down',
    };
    return sectionNames[section];
  }

  closeSuccessModalAndRedirect(): void {
    this.showSuccessModal = false;
    if (!this.isEmbedded) {
      this.router.navigate(['/fitness-plan/templates']); // Redirect to template list only if not embedded
    }
  }

  goBack(): void {
    this.router.navigate(['/fitness-plan/templates']);
  }

  // Method to edit the template
  editTemplate(): void {
    if (this.templateId) {
      this.router.navigate(['/fitness-plan/templates/edit', this.templateId]);
    }
  }

  // Helper to track exercises in ngFor
  trackById(index: number, item: Exercise): number {
    return item.id;
  }

  // Method to ensure each day has exercises in all sections (for loaded templates)
  ensureAllSectionsHaveExercises(): void {
    this.weeklyTemplate.forEach((day, dayIndex) => {
      if (!day.isRestDay) {
        // Check each section and add empty exercise if missing
        this.exerciseSections.forEach((section) => {
          const sectionExercises = this.getExercisesBySection(
            dayIndex,
            section.key
          );
          if (sectionExercises.length === 0) {
            // console.log(
            //   `Adding default exercise for ${section.key} section in day ${dayIndex}`
            // );
            this.addExerciseToDay(dayIndex, section.key);
          }
        });
      }
    });
  }

  // Delete template methods
  showDeleteModal(): void {
    this.showDeleteConfirmation = true;
  }

  closeDeleteModal(): void {
    this.showDeleteConfirmation = false;
  }

  confirmDeleteTemplate(): void {
    if (this.templateId) {
      this.fitnessPlanService
        .deleteFitnessPlanTemplate(this.templateId)
        .subscribe({
          next: (response) => {
            console.log('Template deleted successfully:', response);
            this.showDeleteConfirmation = false;

            // Show success message and redirect
            alert('Template deleted successfully!');
            this.router.navigate(['/fitness-plan/templates']);
          },
          error: (error) => {
            console.error('Error deleting template:', error);
            this.showDeleteConfirmation = false;
            alert('Failed to delete template. Please try again.');
          },
        });
    }
  }

  // Copy day functionality
  openCopyDayDialog(dayIndex: number): void {
    const dialogRef = this.dialog.open(CopyDayDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: {
        weeklyTemplate: this.weeklyTemplate,
        currentDayIndex: dayIndex,
      },
    });

    dialogRef.afterClosed().subscribe((sourceDayIndex: number) => {
      if (sourceDayIndex !== undefined && sourceDayIndex !== null) {
        this.copyDayData(sourceDayIndex, dayIndex);
      }
    });
  }

  copyDayData(sourceDayIndex: number, targetDayIndex: number): void {
    const sourceDay = this.weeklyTemplate[sourceDayIndex];
    const targetDay = this.weeklyTemplate[targetDayIndex];

    // Don't copy if source is a rest day
    if (sourceDay.isRestDay) {
      return;
    }

    // Deep copy exercises from source day
    const copiedExercises = sourceDay.exercises.map((exercise) => ({
      id: Math.max(0, ...targetDay.exercises.map((e) => e.id)) + Math.random(),
      exercise: exercise.exercise,
      exerciseId: exercise.exerciseId,
      section: exercise.section,
      sets: exercise.sets.map((set) => ({
        setNumber: set.setNumber,
        reps: set.reps,
        time: set.time,
        timeMinutes: set.timeMinutes,
        timeSeconds: set.timeSeconds,
        totalSeconds: set.totalSeconds,
        weights: set.weights,
        status: 'new' as const,
      })),
      allotmentId: exercise.allotmentId,
    }));

    // Replace target day exercises with copied exercises
    targetDay.exercises = copiedExercises;
    targetDay.isRestDay = false;

    // console.log(
    //   `Copied exercises from ${sourceDay.dayName} to ${targetDay.dayName}`
    // );
  }
}
